/**
 * «Resoconto ora» (F-051, TODO.md): riservato agli utenti premium e conteggiato
 * sulla quota delle analisi manuali, non su quella dei briefing automatici.
 *
 * **Estratto da `MarketsPage`** per essere usato anche nel dettaglio del singolo
 * mercato: era una funzione interna a quel file, e duplicarla avrebbe prodotto
 * due comportamenti destinati a divergere su un pulsante che può innescare
 * ordini.
 *
 * Le tre regole che porta con sé, tutte già pagate una volta:
 *
 *  1. **Visibile ma disabilitato con la ragione** a chi non è premium (F-051):
 *     «una funzione invisibile non si può nemmeno desiderare».
 *  2. **«avviato» solo se ha davvero avviato qualcosa**: il resoconto di oggi
 *     può esistere già, e in quel caso non parte nessuna analisi. Dirlo
 *     «avviato» comunque è la bugia di F-037.
 *  3. **La forzatura si offre solo dove può funzionare**: su un resoconto già
 *     concluso, non su uno ancora in corso.
 */

import { useState } from 'react';
import { useAuth } from '@/features/auth/auth-context';
import { AutoTradingConfirmModal } from '@/features/auto-trading/AutoTradingConfirmModal';
import { ApiError } from '@/lib/api-client';
import { Button } from '@/ui';
import { useRequestBriefing } from './api';

export type RequestBriefingButtonProps = {
  mic: string;
  name: string;
  watchlistInstrumentCount: number;
};

export function RequestBriefingButton({ mic, name, watchlistInstrumentCount }: RequestBriefingButtonProps) {
  const { user } = useAuth();
  const requestBriefing = useRequestBriefing(mic);
  const [confirming, setConfirming] = useState(false);
  const result = requestBriefing.data;

  const error = requestBriefing.error instanceof ApiError ? requestBriefing.error : null;
  const isPremium = user?.isPremium ?? false;

  // Senza titoli collegati non c'è nulla da riassumere: il backend rifiuterebbe
  // comunque, meglio non offrire l'azione.
  if (watchlistInstrumentCount === 0) return null;

  return (
    <span
      className="markets__request-briefing"
      title={
        isPremium
          ? 'Genera subito un resoconto AI dei titoli in watchlist di questo mercato'
          : 'Il resoconto su richiesta è riservato agli utenti premium'
      }
    >
      <Button
        variant="ghost"
        className="btn--sm"
        disabled={!isPremium}
        loading={requestBriefing.isPending}
        onClick={() => setConfirming(true)}
      >
        Resoconto ora
      </Button>

      {requestBriefing.isSuccess && (
        <span className={result?.created ? 'markets__request-ok' : 'markets__request-note'}>
          {result?.created
            ? 'avviato'
            : result?.alreadyTerminal
              ? 'il resoconto di oggi esiste già ed è concluso'
              : 'il resoconto di oggi è già in corso'}
          {result && !result.created && result.autoTradingRequested && (
            <>
              {' — '}
              {result.autoTradingScheduled
                ? 'auto-trading agganciato al digest'
                : result.autoTradingReason === 'empty_briefing'
                  ? 'nessuna analisi nel resoconto, niente da valutare'
                  : 'auto-trading non agganciato'}
            </>
          )}
        </span>
      )}

      {result && !result.created && result.alreadyTerminal && (
        <>
          {' '}
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={requestBriefing.isPending}
            onClick={() => requestBriefing.mutate({ runAutoTrading: true, force: true })}
          >
            {requestBriefing.isPending ? 'Rigenero…' : 'Forza un nuovo resoconto'}
          </button>
        </>
      )}

      {error && <span className="markets__request-error">{error.message}</span>}

      {confirming && (
        <AutoTradingConfirmModal
          title={`Resoconto ora — ${name}`}
          summary={`Verranno analizzati i ${watchlistInstrumentCount} titoli della tua watchlist collegati a ${name}, poi ne verrà prodotto un resoconto. Consuma la quota manuale di analisi AI, non quella dei briefing automatici.`}
          kind="chain"
          confirmLabel="Genera il resoconto"
          pending={requestBriefing.isPending}
          onClose={() => setConfirming(false)}
          onConfirm={(runAutoTrading) =>
            requestBriefing.mutate({ runAutoTrading }, { onSuccess: () => setConfirming(false) })
          }
        />
      )}
    </span>
  );
}
